import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  notification,
  Row,
  Select,
  Skeleton,
  Space,
  Tooltip,
} from "antd";
import { DeleteTwoTone, EditOutlined, PlusOutlined } from "@ant-design/icons";
import AddRequisition from "./AddRequisition";
import Table, { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../Context/ThemeContext";
import search from "../../Images/search.svg";
import RequisitionService, {
  PriorityLevel,
} from "../../Services/Inventory/RequisitionService";
import { useClient } from "../../Context/ClientContext";
import { dayjs } from "../../../Utilities/dayjs";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import { tablePageSizeOptions } from "../../Common/ThemeSetting";

interface ItemType {
  item: {
    id: string;
    name: string;
  };
  appliedQty: number;
  location: {
    id: string;
    name: string;
  };
}

interface DataType {
  key: string;
  id: string | null;
  companies: string;
  department: string;
  items: ItemType[] | any;
  priority: PriorityLevel;
  expectedDateToBeReceived: string;
}
type NotificationType = "success" | "error";

const Requisition = () => {
  const { themeData } = useTheme();
  const { RangePicker } = DatePicker;
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState(false);

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState<number>(
    themeData?.tablePageSize ?? 15
  );
  const [totalRecords, setTotalRecords] = React.useState<number>(0);
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [editRequisition, setEditRequisition] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [apiResponse, setApiResponse] = useState<DataType[] | any[]>([]);
  const [companyId, setCompanyId] = useState<string>("");
  const [searchRequisition, setSearchRequisition] = useState("");
  const [width, setWidth] = React.useState<any>(window.innerWidth);
  const [priorityFilter, setPriorityFilter] = React.useState<PriorityLevel>(
    PriorityLevel.All
  );

  const [startDate, setStartDate] = React.useState<Date>();
  const [endDate, setEndDate] = React.useState<Date>();
  const [api, contextHolder] = notification.useNotification();
  const { currentClient } = useClient();
  const [filterDate, setFilterDate] = React.useState<any>(
    JSON.parse(localStorage.getItem("LockPeriod")!)
      ? {
          from: JSON.parse(localStorage.getItem("LockPeriod")!).from,
          to: JSON.parse(localStorage.getItem("LockPeriod")!).to,
        }
      : {
          from: dayjs(),
          to: dayjs(),
        }
  );

  React.useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [width]);
  const openNotification = (type: NotificationType, message: string) => {
    api[type]({
      message: "",
      description: message,
      duration: 2,
      closeIcon: false,
      style: {
        width: 350,
      },
    });
  };

  useEffect(() => {
    setLoading(true);
    getRequisitions(currentClient?.id!);
  }, [
    trigger,
    searchRequisition,
    priorityFilter,
    filterDate?.from,
    filterDate?.to,
  ]);

  const getRequisitions = async (companyId: string) => {
    await RequisitionService.getRequisitionList(
      companyId,
      searchRequisition,
      priorityFilter,
      filterDate?.from,
      filterDate?.to
    )
      .then((res: any) => {
        if (res != null || res?.result?.length > 0) {
          const response = res?.result?.map((r: any, i: number) => {
            return {
              key: r?.id,
              id: r?.id,
              sno: i + 1,
              companies: r?.companies,
              items: r?.items,
              priority: r?.priority,
              expectedDateToBeReceived: r?.expectedDateToBeReceived,
            };
          });

          setApiResponse(response);
          setTotalRecords(res?.totalRecords);
          setLoading(false);
        } else if (res.result?.length === 0) {
          setApiResponse([]);
          setTotalRecords(res?.totalRecords);
        }
      })
      .catch((ex: any) => {
        console.log("error", ex);
      });
    setLoading(false);
  };

  const deleteRequisition = async () => {
    let res = await RequisitionService.deleteRequisition(
      currentClient?.id!,
      companyId
    );
    {
      if (res?.status) {
        openNotification("success", "Deleted Successfully");
        getRequisitions(currentClient?.id!);
        setConfirmation(false);
      } else {
        openNotification("error", `${res?.message}`);
      }
    }
  };

  const getQuarterPreset: any = (startMonth: number) => {
    const fromDate = dayjs();
    return [
      fromDate.month(fromDate.month() + startMonth).startOf("month"),
      fromDate.month(fromDate.month() + startMonth + 2).endOf("month"),
    ];
  };
  const thisYear = () => {
    const fromDate = dayjs();
    return [
      fromDate.month(fromDate.month()).startOf("month"),
      fromDate.month(fromDate.month() + 11).endOf("month"),
    ];
  };
  const lastYear = () => {
    const fromDate = dayjs();
    return [
      fromDate.month(fromDate.month() - 12).startOf("month"),
      fromDate.month(fromDate.month() - 1).endOf("month"),
    ];
  };
  const getLast15Days = () => {
    const now = dayjs(); // Get the current date
    const startDate = now.subtract(15, "day").startOf("day"); // Start 15 days ago from today
    const endDate = now.endOf("day"); // End of the current day
    return [startDate, endDate];
  };
  const getLast7Days = () => {
    const now = dayjs(); // Get the current date
    const startDate = now.subtract(7, "day").startOf("day"); // Start 7 days ago from today
    const endDate = now.endOf("day"); // End of the current day
    return [startDate, endDate];
  };

  const getThisMonth = () => {
    const now = dayjs();
    return [now.startOf("month"), now.endOf("month")];
  };
  const getLastMonth = () => {
    const now = dayjs();
    const lastMonth = now.subtract(1, "month");
    return [lastMonth.startOf("month"), lastMonth.endOf("month")];
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: width <= 1440 ? "5%" : "4%",
      render: (_: any, record: DataType, index: number) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {(page - 1) * pageSize + (index + 1)}
        </Skeleton>
      ),
    },
    {
      title: "Company",
      dataIndex: "companies",
      key: "companies",
      width: "10%",
      // sorter: true,
      // ellipsis: {
      //   showTitle: false,
      // },
      // showSorterTooltip: true,
      render: (_: any, record: DataType) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <div>
            {record.companies == currentClient?.id
              ? currentClient.businessName
              : ""}
          </div>
        </Skeleton>
      ),
    },
    {
      title: "Departments",
      dataIndex: "department",
      key: "department",
      width: "10%",
      sorter: true,
      ellipsis: true,
      render: (_: any, record: any) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <div>{record?.department}</div>
        </Skeleton>
      ),
    },
    {
      title: "Items",
      key: "items",
      children: [
        {
          title: "Name",
          dataIndex: "item",
          key: "item",
          width: "8%",
          render: (_: any, record: any) => (
            <Skeleton
              active
              loading={loading}
              paragraph={{ rows: 1 }}
              title={false}
            >
              <div>
                {record?.items?.map((item: any, index: number) => (
                  <div key={index}>
                    {item?.item?.name}
                    <br />
                  </div>
                ))}
              </div>
            </Skeleton>
          ),
        },
        {
          title: "Quantity",
          dataIndex: "appliedQty",
          key: "appliedQty",
          width: "6%",
          render: (_: any, record: any) => (
            <Skeleton
              active
              loading={loading}
              paragraph={{ rows: 1 }}
              title={false}
            >
              <div>
                {record?.items?.map((item: any, index: number) => (
                  <div key={index}>
                    {item?.appliedQty}
                    <br />
                  </div>
                ))}
              </div>
            </Skeleton>
          ),
        },
        {
          title: "Location",
          dataIndex: "location",
          key: "location",
          width: "10%",
          render: (_: any, record: any) => (
            <Skeleton
              active
              loading={loading}
              paragraph={{ rows: 1 }}
              title={false}
            >
              <div>
                {record?.items?.map((item: any, index: number) => (
                  <div key={index}>
                    {item?.location?.name}
                    <br />
                  </div>
                ))}
              </div>
            </Skeleton>
          ),
        },
      ],
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: "5%",
      // sorter: true,
      // ellipsis: true,
      render: (_: any, record: any) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <div>{PriorityLevel[record?.priority]}</div>
        </Skeleton>
      ),
    },
    {
      title: "Expected Delivery Date",
      dataIndex: "expectedDateToBeReceived",
      key: "expectedDateToBeReceived",
      width: "8%",
      render: (_: any, record: any) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <div>
            {dayjs(record?.expectedDateToBeReceived).format("DD/MM/YYYY")}
          </div>
        </Skeleton>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: width <= 1024 ? "5%" : "5%",
      render: (_: any, record: any) => (
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <Row gutter={6}>
            <Col>
              <>
                <Tooltip title="Edit">
                  <EditOutlined
                    style={{
                      color: "#444653",
                    }}
                    className="ca-edit-btn"
                    onClick={() => {
                      setEditRequisition(record?.id);
                      setOpen(true);
                    }}
                  />
                </Tooltip>
              </>
            </Col>
            <Col>
              <Tooltip title="Delete" color="red">
                <DeleteTwoTone
                  className="ca-delete-btn cursor"
                  twoToneColor="#ff879d"
                  onClick={() => {
                    setConfirmation(true);
                    setCompanyId(record?.id);
                  }}
                />
              </Tooltip>
            </Col>
          </Row>
        </Skeleton>
      ),
    },
  ];

  // const tableSort = (pagination?: any, sort?: any, filter?: any) => {
  //   setFilter({
  //     sortCol: filter.column !== undefined ? filter.columnKey : "name",
  //     sortDir:
  //       filter.column !== undefined
  //         ? filter.order === "ascend"
  //           ? "asc"
  //           : "desc"
  //         : "asc",
  //   });
  // };

  const navigate = useNavigate();

  const RedirectToPreviousRoute = () => {
    let xyz: any = localStorage.getItem("id");
    navigate(`/accounts/clients/${xyz}/dashboard`);
  };

  return (
    <div>
      {open && (
        <AddRequisition
          open={open}
          setOpen={setOpen}
          setTrigger={setTrigger}
          notification={openNotification}
          reqId={editRequisition}
        />
      )}
      <Card
        size="small"
        className="TableCard"
        style={{
          marginTop: 10,
          boxShadow: "0px 0px 10px 0px #96A1C84D",
        }}
      >
        <Row justify={"space-between"} style={{ padding: "10px 2px 3px 2px" }}>
          <Col>
            <Space size={width <= 768 ? 280 : 5}>
              <Button
                type="primary"
                onClick={() => {
                  setOpen(true);
                  setEditRequisition("");
                }}
              >
                <PlusOutlined />
                Add Requisition
              </Button>
              {localStorage.getItem("name") ? (
                <>
                  <div className="clientHover" style={{ padding: "5px" }}>
                    <span style={{ cursor: "pointer" }}>Continue with </span>

                    <span
                      onClick={RedirectToPreviousRoute}
                      style={{
                        fontWeight: 500,
                        color: "rgb(68, 70, 83)",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    >
                      {" "}
                      {localStorage.getItem("name")}
                    </span>
                    {/* <span>
                    <RiHistoryFill />
                  </span> */}
                  </div>
                </>
              ) : (
                ""
              )}
              <div className="clientHover" style={{ padding: "5px" }}></div>
            </Space>
          </Col>
          <Col>
            <Row gutter={9}>
              <Col style={{ marginLeft: "10px" }}>
                <Input
                  style={{
                    width: "200px",
                    border: "1px solid #f1f3ff",
                  }}
                  allowClear={true}
                  placeholder="Search here"
                  suffix={
                    loading || searchRequisition ? (
                      ""
                    ) : (
                      <img alt={"search client"} src={search} />
                    )
                  }
                  onChange={(e: any) => {
                    setLoading(true);
                    if (!e.cancelable) {
                      const data = setTimeout(() => {
                        setPage(1);
                        setSearchRequisition(e.target.value);
                      }, 1000);
                      return () => clearTimeout(data);
                    } else {
                      setSearchRequisition(e.target.value);
                    }
                  }}
                />
              </Col>
              <Col>
                <RangePicker
                  disabledDate={(current) =>
                    current < dayjs("1900-01-01", "YYYY-MM-DD").add(-1, "day")
                  }
                  format="DD/MM/YYYY"
                  defaultValue={[
                    dayjs(filterDate?.from, "DD/MM/YYYY"),
                    dayjs(filterDate?.to, "DD/MM/YYYY"),
                  ]}
                  style={{
                    width:
                      width <= 464
                        ? "210px"
                        : width === 375
                        ? "200px"
                        : "250px",
                    border: "1px solid #f1f3ff",
                  }}
                  onChange={(e: any) => {
                    if (e === null) {
                      setFilterDate({});
                    } else {
                      setFilterDate({
                        ...filterDate,
                        from: dayjs(e[0]).format("DD/MM/YYYY"),
                        to: dayjs(e[1]).format("DD/MM/YYYY"),
                      });
                      localStorage.setItem(
                        "LockPeriod",
                        JSON.stringify({
                          ...filterDate,
                          from: dayjs(e[0]).format("DD/MM/YYYY"),
                          to: dayjs(e[1]).format("DD/MM/YYYY"),
                        })
                      );
                      openNotification("success", `Period locked.`);
                    }
                  }}
                  presets={[
                    {
                      label: "Today",
                      value: [dayjs().add(0, "d"), dayjs()],
                    },
                    {
                      label: "Last 7 Days",
                      value: getLast7Days(),
                    },
                    {
                      label: "Last 15 Days",
                      value: getLast15Days(),
                    },
                    {
                      label: "This Month",
                      value: getThisMonth(),
                    },
                    {
                      label: "Last Month",
                      value: getLastMonth(),
                    },
                    {
                      label: "Quarter 1",
                      value: getQuarterPreset(0),
                    },
                    {
                      label: "Quarter 2",
                      value: getQuarterPreset(3),
                    },
                    {
                      label: "Quarter 3",
                      value: getQuarterPreset(6),
                    },
                    {
                      label: "Quarter 4",
                      value: getQuarterPreset(9),
                    },
                    {
                      label: "This Year",
                      value: thisYear(),
                    },
                    {
                      label: "Last Year",
                      value: lastYear(),
                    },
                  ]}
                />
              </Col>
              <Col>
                <Select
                  style={{ width: "110px" }}
                  placeholder={PriorityLevel[4]}
                  onChange={(e: PriorityLevel) => {
                    setPriorityFilter(e);
                  }}
                  options={[
                    {
                      label: "Low",
                      value: PriorityLevel.Low,
                    },
                    {
                      label: "Medium",
                      value: PriorityLevel.Medium,
                    },
                    {
                      label: "High",
                      value: PriorityLevel.High,
                    },
                    {
                      label: "Unspecified",
                      value: PriorityLevel.Unspecified,
                    },
                    {
                      label: "All",
                      value: PriorityLevel.All,
                    },
                  ]}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Table
          className={`Tabel-style table-${
            themeData?.componentSize ?? "middle"
          }`}
          columns={columns}
          dataSource={
            loading
              ? (Array.from({ length: 20 }, (_, index) => ({
                  key: `loading-${index}`,
                })) as DataType[])
              : apiResponse
          }
          locale={{
            triggerDesc: "",
            triggerAsc: "",
            cancelSort: "",
          }}
          //onChange={tableSort}
          pagination={{
            total: totalRecords,
            current: page,
            pageSize: pageSize,
            showSizeChanger: true,
            onChange: (page: number, pageSize: number) => {
              setPage(page);
              setPageSize(pageSize);
            },
            pageSizeOptions: tablePageSizeOptions,
            showTotal: (totalRecords, page) =>
              `${page[0]}-${page[1]} of ${totalRecords} items`,
          }}
          scroll={
            pageSize > 15 ? { x: 1500, y: window.innerHeight - 300 } : undefined
          }
        />
      </Card>
      {contextHolder}
      {confirmation && (
        <ConfirmationModal
          open={confirmation}
          onNo={() => setConfirmation(false)}
          onYes={() => {
            deleteRequisition();
          }}
          text={"Are you sure you want to delete this row ? "}
        />
      )}
    </div>
  );
};

export default Requisition;
